import { StateCreator } from "zustand";
import { BoundStore } from "../bound.store";
import {
  FilterSelection,
  FilterTarget,
  FilterTargetsByItinerary,
  FilterTargetsSlice,
} from "./filterTargets.interface";

/**
 * The all-on default: itinerary included, nothing excluded. Represented by absence in the store.
 * A single shared instance is returned so selectors that fall back to it stay referentially stable
 * across renders (safe to share because selections are only ever read or replaced immutably, never
 * mutated in place — do not mutate this object).
 */
const DEFAULT_FILTER_SELECTION: FilterSelection = {
  enabled: true,
  excludedSections: [],
  excludedCategories: [],
  showPublic: true,
};
export function defaultFilterSelection(): FilterSelection {
  return DEFAULT_FILTER_SELECTION;
}

/** Resolves the stored selection for an itinerary/target, or the all-on default if none is stored. */
export function resolveFilterSelection(
  filters: FilterTargetsByItinerary,
  itineraryId: number,
  target: FilterTarget,
): FilterSelection {
  return filters[itineraryId]?.[target] ?? defaultFilterSelection();
}

/** True when a selection matches the all-on default and therefore doesn't need persisting. */
function isDefaultSelection(selection: FilterSelection): boolean {
  return (
    selection.enabled &&
    selection.excludedSections.length === 0 &&
    selection.excludedCategories.length === 0 &&
    selection.showPublic
  );
}

/**
 * Immutably update a single (itinerary, target) selection. A selection (and its itinerary record)
 * that returns to the all-on default is pruned, so the default behaviour stores nothing.
 */
function updateSelection(
  filters: FilterTargetsByItinerary,
  itineraryId: number,
  target: FilterTarget,
  updater: (selection: FilterSelection) => FilterSelection,
): FilterTargetsByItinerary {
  const record = filters[itineraryId] ?? {};
  const next = updater(record[target] ?? defaultFilterSelection());

  const nextRecord: Partial<Record<FilterTarget, FilterSelection>> = { ...record };
  if (isDefaultSelection(next)) {
    delete nextRecord[target];
  } else {
    nextRecord[target] = next;
  }

  const nextFilters = { ...filters };
  if (Object.keys(nextRecord).length === 0) {
    delete nextFilters[itineraryId];
  } else {
    nextFilters[itineraryId] = nextRecord;
  }
  return nextFilters;
}

/** Add `id` if absent, remove it if present. */
function toggleId<T>(list: T[], id: T): T[] {
  return list.includes(id) ? list.filter((existing) => existing !== id) : [...list, id];
}

export const createFilterTargetsSlice: StateCreator<
  BoundStore,
  [["zustand/devtools", never]],
  [],
  FilterTargetsSlice
> = (set, get) => ({
  filterTargets: {},

  getFilterSelection: (itineraryId, target) => resolveFilterSelection(get().filterTargets, itineraryId, target),

  setFilterEnabled: (itineraryId, target, enabled) =>
    set((state) => ({
      ...state,
      filterTargets: updateSelection(state.filterTargets, itineraryId, target, (selection) => ({
        ...selection,
        enabled,
      })),
    })),

  setFilterShowPublic: (itineraryId, target, showPublic) =>
    set((state) => ({
      ...state,
      filterTargets: updateSelection(state.filterTargets, itineraryId, target, (selection) => ({
        ...selection,
        showPublic,
      })),
    })),

  toggleFilterSection: (itineraryId, target, sectionId) =>
    set((state) => ({
      ...state,
      filterTargets: updateSelection(state.filterTargets, itineraryId, target, (selection) => ({
        ...selection,
        excludedSections: toggleId(selection.excludedSections, sectionId),
      })),
    })),

  toggleFilterCategory: (itineraryId, target, categoryId) =>
    set((state) => ({
      ...state,
      filterTargets: updateSelection(state.filterTargets, itineraryId, target, (selection) => ({
        ...selection,
        excludedCategories: toggleId(selection.excludedCategories, categoryId),
      })),
    })),

  resetFilter: (itineraryId, target) =>
    set((state) => ({
      ...state,
      filterTargets: updateSelection(state.filterTargets, itineraryId, target, () => defaultFilterSelection()),
    })),
});
